import { Skeleton } from 'antd';
import { SortingParams, SortType } from 'modules/types/PagingParams';
import { DownloadButton } from 'modules/utils';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiBills } from 'modules/api';
import { CurrencyContainer } from 'modules/ui/table/currencyContainer';
import JSZip from 'jszip';
import { useAppDispatch, useAppSelector } from '../../hooks/appHooks';
import { BillsSelectors, BillsThunks } from '../redux';
import { getBillStatusIcon } from './helper';
import { Checkbox } from '../../ui';
import { ColumnFilters, setCheckedBills } from '../redux/slice';
import { ToastType } from '../../ui/toast';
import { useToastContainer } from '../../layout/toastContainer';
import { TableColumn } from '../../ui/table';

export const useBillsActions = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { addToast } = useToastContainer();

  const bills = useAppSelector(BillsSelectors.selectBills);
  const totalPages = useAppSelector(BillsSelectors.selectTotalPageCount);
  const loading = useAppSelector(BillsSelectors.selectLoadingBills);
  const totalBills = useAppSelector(BillsSelectors.selectTotalBillCount);
  const checkedBills = useAppSelector(BillsSelectors.selectCheckedBills);
  const billFilters = useAppSelector(BillsSelectors.selectBillFilters);
  const checkboxFilters = useAppSelector(BillsSelectors.selectCheckboxFilters);

  const fetchColumnCheckbox = (column: TableColumn) => {
    if (
      column.accessor === 'status' &&
      !checkboxFilters?.find((filter) => filter.column === column.label)
    )
      return dispatch(BillsThunks.fetchStatusCheckboxesValues());
    if (
      column.accessor === 'contractNumber' &&
      !checkboxFilters?.find((filter) => filter.column === column.label)
    )
      return dispatch(BillsThunks.fetchContractNumberCheckboxesValues());
    return undefined;
  };

  const showFilterForColumn = (
    accessor: string,
    filterType: string,
  ): boolean => {
    if (
      (accessor === 'date' || accessor === 'dueDate') &&
      filterType === 'calendar'
    )
      return true;
    if (
      (accessor === 'number' ||
        accessor === 'contractNumber' ||
        accessor === 'description' ||
        accessor === 'status') &&
      filterType === 'search'
    )
      return true;
    if (
      (accessor === 'contractNumber' || accessor === 'status') &&
      filterType === 'checkbox'
    )
      return true;
    return false;
  };

  const showSorting = (accessor: string): boolean =>
    accessor === 'date' ||
    accessor === 'number' ||
    accessor === 'contractNumber' ||
    accessor === 'dueDate' ||
    accessor === 'amount' ||
    accessor === 'installments' ||
    accessor === 'status';

  const fetchMoreBills = useCallback(
    (
      page: number,
      sort?: SortingParams[],
      search?: string,
      rowCount?: number,
    ) => {
      dispatch(
        BillsThunks.fetchBills({
          page,
          size: rowCount,
          search,
          sort,
        }),
      );
    },
    [dispatch],
  );

  const fetchMoreItemsWithFilters = useCallback(
    (
      page: number,
      sort?: SortingParams[],
      search?: string,
      rowCount?: number,
      filter?: { column: string; filters: ColumnFilters }[],
    ) => {
      dispatch(
        BillsThunks.filterBills({
          page,
          size: rowCount,
          search,
          sort,
          filter: filter?.map((filterColumn) => ({
            ...filterColumn,
            filters: {
              ...filterColumn.filters,
              checkbox:
                filterColumn.filters.checkbox?.map(
                  (checkbox) => checkbox.value ?? '',
                ) ?? [],
            },
          })),
        }),
      );
    },
    [dispatch],
  );

  const getBillsColumns = useMemo(
    () => [
      {
        label: t('billsPage.table.checkbox'),
        accessor: 'checkbox',
        mobileOnly: true,
      },
      {
        label: t('billsPage.table.date'),
        accessor: 'date',
        sort: SortType.DEFAULT,
      },
      {
        label: t('billsPage.table.number'),
        accessor: 'number',
        sort: SortType.DEFAULT,
      },
      {
        label: t('billsPage.table.contractNumber'),
        accessor: 'contractNumber',
        sort: SortType.DEFAULT,
      },
      {
        label: t('billsPage.table.dueDate'),
        accessor: 'dueDate',
        sort: SortType.DEFAULT,
      },
      {
        label: t('billsPage.table.amount'),
        accessor: 'amount',
        sort: SortType.DEFAULT,
      },
      {
        label: t('billsPage.table.installmentNumber'),
        accessor: 'installments',
        sort: SortType.DEFAULT,
      },
      {
        label: t('billsPage.table.description'),
        accessor: 'description',
        sort: SortType.DEFAULT,
      },
      {
        label: t('billsPage.table.status'),
        accessor: 'status',
        sort: SortType.DEFAULT,
      },
    ],
    [t],
  );
  const onDownloadButtonClick = () => async () =>
    checkedBills.length > 1
      ? ApiBills.getBillsZip(checkedBills).then(async (response) => {
          const zip = new JSZip();
          await zip.loadAsync(response.data).then((content) => {
            const failedBills = checkedBills.filter(
              (checkedBill) =>
                Object.getOwnPropertyNames(content.files).findIndex(
                  (fileName) => fileName.trim().includes(checkedBill.trim()),
                ) < 0,
            );
            if (failedBills.length > 0) {
              addToast({
                title: t('billsPage.download.failedToastTitle'),
                text: `${t(
                  'billsPage.download.failedToastText',
                )} ${failedBills.join(', ')}`,
                type: ToastType.Urgent,
              });
            } else {
              addToast({
                title: t('billsPage.download.successToastTitle'),
                text: '',
                type: ToastType.Info,
              });
            }
            if (Object.getOwnPropertyNames(content.files).length === 0) {
              response.data = undefined;
            }
          });
          return response.data;
        })
      : ApiBills.getBillPdf(checkedBills[0]);

  const onCheckAllButtonClick = (checked: boolean) => {
    if (checked) {
      dispatch(setCheckedBills(bills?.map((bill) => bill.number) ?? []));
    } else {
      dispatch(setCheckedBills([]));
    }
  };

  const getBillsObject = useMemo(
    () =>
      bills?.map((bill) => ({
        ...bill,
        checkbox: (
          <Checkbox
            text=""
            onCheckboxClicked={(isChecked: boolean) => {
              if (checkedBills.indexOf(bill.number) < 0 && isChecked) {
                dispatch(setCheckedBills([...checkedBills, bill.number]));
              } else if (checkedBills.indexOf(bill.number) > -1 && !isChecked) {
                dispatch(
                  setCheckedBills(
                    checkedBills.filter((b) => b !== bill.number),
                  ),
                );
              }
            }}
            shouldBeChecked={checkedBills.indexOf(bill.number) > -1}
          />
        ),
        status: getBillStatusIcon(bill.status),
        amount: <CurrencyContainer value={bill.amountEur} />,
        download: (
          <DownloadButton
            apiCall={async () => ApiBills.getBillPdf(bill.number)}
            fileName={`racun-${bill.number}.pdf`}
          />
        ),
      })) || [],
    [bills, checkedBills],
  );

  const getSkeletonObject = useMemo(
    () =>
      [...Array(10)].map(() => ({
        checkbox: <Skeleton.Input active size="small" />,
        date: <Skeleton.Input active size="small" />,
        number: <Skeleton.Input active size="small" />,
        contractNumber: <Skeleton.Input active size="small" />,
        dueDate: <Skeleton.Input active size="small" />,
        amount: <Skeleton.Input active size="small" />,
        installmentNumber: <Skeleton.Input active size="small" />,
        description: <Skeleton.Input active size="small" />,
        status: <Skeleton.Input active size="small" />,
        newBill: <Skeleton.Input active size="small" />,
      })) || [],
    [],
  );

  const tableRows = getBillsObject;
  const [tableColumns, setTableColumns] = useState(getBillsColumns);
  const emptyMessageText = t('billsPage.empty.noBills');
  const columnsMaxWidth = '2.6rem';

  return {
    skeletonObject: getSkeletonObject,
    fetchMoreData: fetchMoreBills,
    onDownloadButtonClick,
    onCheckAllButtonClick,
    checkedBills,
    tableFilters: billFilters,
    checkboxFilters,
    totalPages,
    totalBills,
    loading,
    tableColumns,
    tableRows,
    emptyMessageText,
    columnsMaxWidth,
    setTableColumns,
    fetchMoreItemsWithFilters,
    fetchColumnCheckbox,
    showFilterForColumn,
    showSorting,
  };
};
